import { ElementRef, Inject, Injectable, Injector } from '@angular/core';
import { CommonBaseService } from '@CaseOne/Common/common-base-service.class/common-base-service.class';
import { remove } from 'lodash';
import { DOCUMENT } from '@angular/common';
import { UIRouter } from '@uirouter/angular';
import {
	ICommonContextMenuRegisterOptions,
	ICommonContextMenuRegistryItem, TCommonContextMenuRegistryElementOrSelector, TCommonContextMenuRegistryItemEntity
} from '@CaseOne/Common/context-menu-registry/common-context-menu-regisrty.interfaces';
import { CommonGUIDService } from '@CaseOne/Common/guid.service/guid.service';


@Injectable({
	providedIn: 'root',
})
export class CommonContextMenuRegistryService extends CommonBaseService {
	private openContextMenus: ICommonContextMenuRegistryItem[] = [];

	public get isOpen (): boolean {
		return this.openContextMenus.length > 0;
	}

	constructor(
		protected injector: Injector,
		private uiRouter: UIRouter,
		@Inject(DOCUMENT) private document: Document,
	) {
		super(injector);

		this.dismissClickHandler = this.dismissClickHandler.bind(this);

		// add mousedown event listener on next event loop tick to ignore current mousedown event
		// capture: true is required, otherwise the event does not reach the listener, if the element that triggered the event is destroyed
		this.document.body.addEventListener('mousedown', this.dismissClickHandler, {capture: true});

		this.uiRouter.transitionService.onBefore(
			{},
			(transition) => {
				if (transition.from().name !== transition.to().name && this.isOpen) {
					this.closeAllContextMenus();
				}
			},
		);
	}

	registerContextMenu (options: ICommonContextMenuRegisterOptions) {
		if (options.id) {
			this.closeContextMenuById(options.id);
		}

		if (options.excludeFromRegistry) {
			return;
		}

		if (!options.parentContextMenuEntity) {
			this.closeAllContextMenus();
		} else {
			this.closeContextMenusFromParent(options.parentContextMenuEntity);
		}

		const newCMItem: ICommonContextMenuRegistryItem = {
			uuid: CommonGUIDService.getGUID(),
			id: options.id,
			contextMenuEntity: options.contextMenuEntity,
			contextMenuHTMLElement: options.contextMenuHTMLElement,
			parentContextMenuEntity: options.parentContextMenuEntity,
			close: options.close,
			afterClose: options.afterClose,
			afterOpen: options.afterOpen,
			dismissClickIgnoreElements: options.dismissClickIgnore?.map((element) => this.convertToHTMLElement(element)),
		};

		if (newCMItem.afterOpen) {
			const afterOpenSubscription = newCMItem.afterOpen
				.subscribe((cmOptions) => {
					newCMItem.contextMenuEntity = cmOptions.contextMenuEntity;
					newCMItem.contextMenuHTMLElement = cmOptions.contextMenuHTMLElement;

					afterOpenSubscription.unsubscribe();
				});
		}

		const afterCloseSubscription = newCMItem.afterClose
			.subscribe(() => {
				remove(this.openContextMenus, (cm) => cm.uuid === newCMItem.uuid);

				afterCloseSubscription.unsubscribe();
			});

		this.openContextMenus.push(newCMItem);
	}

	closeAllContextMenus() {
		this.openContextMenus.forEach((cm) => {
			cm.close();
		});
	}

	private closeContextMenusFromParent (parent: TCommonContextMenuRegistryItemEntity) {
		const targetCMIndex = this.openContextMenus.findIndex((cm) => cm.contextMenuEntity === parent);

		for (let i = this.openContextMenus.length - 1; i > targetCMIndex; i--) {
			this.openContextMenus[i].close();
		}
	}

	private closeContextMenuById (id: string) {
		this.openContextMenus.forEach((cm) => {
			if (cm.id === id) {
				cm.close();
			}
		});
	}

	private dismissClickHandler (event: MouseEvent) {
		if (this.isOpen) {
			const cm = this.openContextMenus[this.openContextMenus.length - 1];
			const parentElmIsFind = this.findParentElement(event.target as HTMLElement, cm.contextMenuHTMLElement, cm.dismissClickIgnoreElements);

			if (!parentElmIsFind) {
				this.openContextMenus[this.openContextMenus.length - 1].close();
			}
		}
	}

	private findParentElement (
		node: HTMLElement,
		element: HTMLElement,
		dismissClickIgnore: HTMLElement[] = [],
	) {
		const compareWithNode = (x) => x === node;

		while (node !== this.document.documentElement) {
			if (node && node !== element && !dismissClickIgnore.find(compareWithNode)) {
				node = node.parentNode as HTMLElement;
			} else {
				return true;
			}
		}

		return false;
	}

	private convertToHTMLElement(element: TCommonContextMenuRegistryElementOrSelector): HTMLElement {
		if (typeof element === 'string') {
			return this.document.documentElement.querySelector(element);
		} else if (element instanceof ElementRef) {
			return element.nativeElement;
		}

		return element;
	}
}
